/**
 * Which lists the day uses (user, 19/10/2026: "I want to know which lists
 * are being used"): one row per list — its group(s), the containers that
 * send to it, how many drops and SMS, the gateways, and its first and last
 * send — in the order the day reaches them. Skipped drops do not count.
 * For Media Devoted | Created By Ciro Autiero
 */
import type { DayPlanPreview } from '../../../drop-generator.types';
import { listKey, liveDrop } from '../summary/timeline-facts';

export interface Chip {
  name: string;
  color: string;
}

export interface ListUse {
  key: string;
  name: string;
  groups: Chip[];
  containers: Chip[];
  drops: number;
  sms: number;
  gateways: string[];
  firstAt: string;
  lastAt: string;
}

const addChip = (chips: Chip[], chip: Chip) => (chips.some((c) => c.name === chip.name) ? chips : [...chips, chip]);

export function listsInUse(preview: DayPlanPreview): ListUse[] {
  const uses = new Map<string, ListUse>();
  for (const container of preview.containers)
    for (const drop of container.drops.filter(liveDrop)) {
      const key = listKey(drop);
      if (key) uses.set(key, withDrop(uses.get(key) ?? blank(key, drop), container, drop));
    }
  return [...uses.values()].sort((a, b) => a.firstAt.localeCompare(b.firstAt) || a.name.localeCompare(b.name));
}

type Container = DayPlanPreview['containers'][number];
type Drop = Container['drops'][number];

const blank = (key: string, drop: Drop): ListUse => ({
  key,
  name: drop.listName ?? `#${drop.listWiserId}`,
  groups: [],
  containers: [],
  drops: 0,
  sms: 0,
  gateways: [],
  firstAt: drop.atUtc,
  lastAt: drop.atUtc,
});

function withDrop(use: ListUse, container: Container, drop: Drop): ListUse {
  const group = container.groups.find((g) => g.listGroupId === drop.groupId);
  return {
    ...use,
    drops: use.drops + 1,
    sms: use.sms + drop.amount,
    groups: group ? addChip(use.groups, { name: group.groupName, color: group.groupColor }) : use.groups,
    containers: addChip(use.containers, { name: container.name, color: container.color }),
    gateways: drop.routeName && !use.gateways.includes(drop.routeName) ? [...use.gateways, drop.routeName] : use.gateways,
    firstAt: drop.atUtc < use.firstAt ? drop.atUtc : use.firstAt,
    lastAt: drop.atUtc > use.lastAt ? drop.atUtc : use.lastAt,
  };
}
